import { useState, useCallback } from 'react';
import { ReactFlow, Controls, useNodesState, useEdgesState, addEdge } from '@xyflow/react';
import { IconDatabase, IconFile } from '@tabler/icons-react';
import { Modal } from '@mantine/core';

import '@xyflow/react/dist/base.css';
import '../css/index.css';

import TurboNode from './TurboNode.jsx';
import TurboEdge from './TurboEdge.jsx';

import dagre from 'dagre';
import DisplayProofPage from '../route/DisplayProofPage.jsx';

const nodeTypes = { turbo: TurboNode };
const edgeTypes = { turbo: TurboEdge };

const defaultEdgeOptions = {
  type: 'turbo',
  markerEnd: 'edge-circle'
};

// Configure Dagre
const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

// Function to calculate layout using Dagre
const calculateDagreLayout = (nodes, edges) => {
  dagreGraph.setGraph({ rankdir: 'LR', marginx: 190, marginy: 190 }); // LR: Left-to-right layout

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 300, height: 100 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate the layout
  dagre.layout(dagreGraph);

  // Update node positions based on Dagre's output
  return nodes.map((node) => {
    const { x, y } = dagreGraph.node(node.id);
    return {
      ...node,
      position: { x, y },
      positionAbsolute: { x, y }
    };
  });
};

function DisplayFlow({ connections, linkedIds, uid }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNodeData, setSelectedNodeData] = useState(null);

  // Transform linkedIds to nodes
  const nodes = linkedIds.map((linkedId) => {
    const isMainNode = linkedId.id === uid;

    return {
      id: linkedId.id,
      data: {
        icon: linkedId.type === 'file' ? <IconFile /> : <IconDatabase />,
        title: linkedId.id,
        subline: linkedId.type
      },
      style: {
        backgroundColor: isMainNode ? '#ffcc00' : '#444'
      },
      type: 'turbo'
    };
  });

  // Transform connections to edges
  const edges = connections.map((connection) => ({
    id: `${connection.source}-${connection.target}`,
    source: connection.source,
    target: connection.target
  }));

  // Calculate node positions using Dagre
  const layoutedNodes = calculateDagreLayout(nodes, edges);
  // eslint-disable-next-line
  const [nodesState, setNodes, onNodesChange] = useNodesState(layoutedNodes);
  const [edgesState, setEdges, onEdgesChange] = useEdgesState(edges);

  const onConnect = useCallback((params) => setEdges((els) => addEdge(params, els)), []);

  const handleNodeClick = (_, node) => {
    setSelectedNodeData(node.data); // Set the clicked node's data
    setModalOpen(true); // Open the modal
  };

  return (
    <>
      <ReactFlow
        nodes={nodesState}
        edges={edgesState}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick} // Handle node click
        fitView
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}>
        <Controls showInteractive={false} />
        <svg>
          <defs>
            <linearGradient id="edge-gradient">
              <stop offset="0%" stopColor="#ae53ba" />
              <stop offset="100%" stopColor="#2a8af6" />
            </linearGradient>
          </defs>
        </svg>
      </ReactFlow>

      <Modal size="auto" opened={modalOpen} onClose={() => setModalOpen(false)}>
        {selectedNodeData && (
          <>
            <DisplayProofPage setUid={selectedNodeData.title} />
          </>
        )}
      </Modal>
    </>
  );
}

export default DisplayFlow;
